import React, { useMemo, useEffect, useState } from 'react';
import {
	Box,
	Button,
	Dialog,
	DialogTitle,
	DialogActions,
	DialogContent,
	CircularProgress,
	FormHelperText,
	TextField,
	FormLabel,
	Typography,
	Stack,
} from '@mui/material';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { closeDialog } from '@/features/dialog/dialogSlice';
import { showToast } from '@/features/toast/toastSlice';
import { useFormikHelper } from '@/hooks/useFormikHelper';
import { triggerRefresh } from '@/features/table/attachmentsTableSlice';
import { specialChars, specialCharsGlobal } from '@/utils/constants';
import validationSchema from './validationSchema';
import folderValidationSchema, {
	FOLDER_NAME as folderName,
	ERROR_MESSAGE,
	FILE_NAME_MAX,
	FOLDER_NAME_MAX,
} from '../AddFolderSharepointDialog/validationSchema';
import {
	useGetFoldersAndFilesQuery,
	useAddMetadataToFileMutation,
	useRenameSharepointItemMutation,
} from '@/features/msGraph/msGraphApi';

const FOLDER_NAME = 'name';
const DESCRIPTION = 'description';
const RENAME_FILE = 'renameFile';

const getUpdateErrorMessage = (error) => {
	const graphError = error?.response?.data?.error?.message;

	if (typeof graphError === 'string') return graphError;

	return (
		graphError?.value ??
		error?.response?.data?.error?.message?.value ??
		error?.message ??
		'Please try again.'
	);
};

export const RenameFileDialog = () => {
	const [renameSharepointItem] = useRenameSharepointItemMutation();
	const [addMetadataToFile] = useAddMetadataToFileMutation();

	const dialog = useSelector((state) => state.dialog);

	const open = dialog?.open?.[RENAME_FILE];
	const data = dialog?.data?.[RENAME_FILE];

	const selectedFile = open ? data?.file : null;

	const isFolder = selectedFile?.isFolder;
	const itemType = isFolder ? 'Folder' : 'File';
	const isUpdateFile = !isFolder;

	const getFileType = () => {
		const fileEnding = isFolder ? [] : (selectedFile?.name ?? '.').split('.');
		return `.${fileEnding?.[fileEnding.length - 1]}`;
	};

	const nameMax = isFolder ? FOLDER_NAME_MAX : FILE_NAME_MAX;

	const { data: parentFolder, ...parentFolderFilesQuery } = useGetFoldersAndFilesQuery(
		{
			itemId: selectedFile?.parentReference?.id,
			driveId: selectedFile?.parentReference?.driveId,
			recursive: false,
		},
		{
			skip:
				!isFolder ||
				!selectedFile?.parentReference?.driveId ||
				!selectedFile?.parentReference?.id,
		},
	);

	const existingFolders = useMemo(
		() =>
			isFolder
				? (parentFolder?.folders ?? [])
						.filter((f) => f.id !== selectedFile?.id)
						.map((f) => f.name)
				: [],
		[isFolder, parentFolder],
	);

	const dispatch = useDispatch();

	const updateFileName = async () => {
		const nameValue = formik.values[FOLDER_NAME];
		console.log('NAME VALUE', nameValue);
		const previousType = getFileType();

		const cleanedName = nameValue.trim().replace(specialCharsGlobal, '').slice(0, 250);
		console.log('cleaned name', cleanedName);
		const type = !isFolder && !cleanedName.endsWith(previousType) ? previousType : '';
		console.log('type', type, { isFolder, endsWithType: cleanedName.endsWith(previousType) });

		const newName =
			cleanedName.replace(type, '').length === 0
				? selectedFile?.name
				: `${cleanedName}${type}`;

		console.log('new name', newName);
		if (newName != selectedFile?.name) {
			await renameSharepointItem({
				driveId: selectedFile.parentReference.driveId,
				itemId: selectedFile.id,
				name: newName,
			}).unwrap();
			return true;
		} else {
			return false;
		}
	};

	const updateDescription = async () => {
		if (isFolder) return false;

		const description = (formik.values[DESCRIPTION] ?? '').trim();
		const currentDescription = (selectedFile?.description ?? '').trim();

		if (description === currentDescription) return false;

		await addMetadataToFile({
			itemId: selectedFile.id,
			driveId: selectedFile.parentReference.driveId,
			metadata: {
				_ExtendedDescription: description,
			},
		}).unwrap();

		return true;
	};

	const initialValues = useMemo(
		() => ({
			[FOLDER_NAME]: selectedFile?.name ?? '',
			[DESCRIPTION]: selectedFile?.description ?? '',
			[ERROR_MESSAGE]: '',
		}),
		[selectedFile?.name, selectedFile?.description],
	);

	const formik = useFormik({
		initialValues,
		validationSchema,
		validateOnBlur: true,
		enableReinitialize: true,
		onSubmit: async (_values, { resetForm, setFieldValue, setSubmitting }) => {
			setFieldValue(ERROR_MESSAGE, '');
			if (isFolder) {
				const valid = await validateFolder();
				if (!valid) {
					setSubmitting(false);
					return;
				}
			}
			try {
				const renamed = await updateFileName();
				const descriptionUpdated = await updateDescription();
				const updated = renamed || descriptionUpdated;

				if (updated) {
					dispatch(
						showToast({
							message:
								isUpdateFile && renamed && descriptionUpdated
									? 'File updated!'
									: isUpdateFile && descriptionUpdated
										? 'Description updated!'
										: `${itemType} name updated!`,
							success: true,
						}),
					);
					dispatch(triggerRefresh());
				}
				resetForm();
				handleClose();
			} catch (error) {
				console.log('error?.response?.data', error?.response?.data);
				const errorMessage = getUpdateErrorMessage(error);
				console.log('🙅 ~ ERROR UPDATING METADATA', error);
				dispatch(
					showToast({
						message: `Failed to update ${isUpdateFile ? 'file' : `${itemType} name`}: ${errorMessage}`,
						error: true,
					}),
				);
				setFieldValue(ERROR_MESSAGE, errorMessage);
			} finally {
				setSubmitting(false);
			}
		},
	});

	const validateFolder = async () => {
		try {
			await folderValidationSchema(existingFolders).validate(
				{ [folderName]: formik.values[FOLDER_NAME] },
				{ strict: true },
			);
			return true;
		} catch (e) {
			console.log('ERROR', e);
			setFolderError(Array.isArray(e?.errors) ? e?.errors.join(', ') : 'Validation error');
			return false;
		}
	};
	const handleClose = () => {
		formik.resetForm();
		dispatch(closeDialog('renameFile'));
	};

	const [folderError, setFolderError] = useState('');

	const isError =
		formik.touched[FOLDER_NAME] &&
		((isFolder && Boolean(folderError)) || Boolean(formik.errors[FOLDER_NAME]));

	const dialogTitle = isUpdateFile ? 'Update File' : `Rename ${itemType}`;
	const submitLabel = isUpdateFile ? 'Update' : 'Rename';

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			hideBackdrop={true}
			sx={{
				zIndex: (theme) => theme.zIndex.snackbar + 1,
				padding: '1em',
				'& .MuiDialog-paper': {
					width: '60%',
				},
			}}
		>
			<DialogTitle>{dialogTitle}</DialogTitle>
			<DialogContent sx={{ paddingBottom: '2em' }}>
				<FormLabel required>Name</FormLabel>
				<TextField
					fullWidth
					multiline
					name={FOLDER_NAME}
					{...formik.getFieldProps(FOLDER_NAME)}
					error={isError}
					// helperText={getErrorMessage(FOLDER_NAME)}
					disabled={formik.isSubmitting}
					{...(isFolder && {
						onBlur: async (e) => {
							formik.getFieldProps(FOLDER_NAME).onBlur(e);
							await validateFolder();
						},
					})}
					required
				/>
				<Stack direction='row' justifyContent='space-between'>
					<FormHelperText error={isError}>
						{isFolder && Boolean(folderError)
							? folderError
							: formik.errors[FOLDER_NAME]}
					</FormHelperText>
					<FormHelperText
						error={formik.values[FOLDER_NAME].length > nameMax}
						sx={{
							...(formik.values[FOLDER_NAME].length >= nameMax && {
								fontWeight: 'bold',
							}),
							mr: '14px',
						}}
					>
						<span>{formik.values[FOLDER_NAME].length}</span>
						{`/${nameMax}`}
					</FormHelperText>
				</Stack>
				{isUpdateFile && (
					<>
						<FormLabel>Description</FormLabel>
						<TextField
							fullWidth
							multiline
							minRows={3}
							name={DESCRIPTION}
							{...formik.getFieldProps(DESCRIPTION)}
							disabled={formik.isSubmitting}
						/>
					</>
				)}
				{formik.values[ERROR_MESSAGE] && (
					<FormHelperText error>{formik.values[ERROR_MESSAGE]}</FormHelperText>
				)}
				<Typography
					variant='caption'
					sx={{
						display: 'block',
						mt: '1.5em',
						color: 'text.secondary',
					}}
				>
					<span>
						{`${itemType} names cannot contain the following characters: `}
						<Box component='span' sx={{ fontWeight: 600 }}>
							{'< > : " \' /\\ | ? % *'}
						</Box>
						{' or end in a .'}
					</span>
				</Typography>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={formik.isSubmitting} color='primary'>
					Cancel
				</Button>
				<Button
					disabled={formik.values[FOLDER_NAME].length === 0}
					variant='contained'
					color='primary'
					onClick={() => formik.handleSubmit()}
				>
					{formik.isSubmitting ? (
						<CircularProgress size='2em' sx={{ color: '#FFFFFF' }} />
					) : (
						submitLabel
					)}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
